/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type MouseEvent, useState } from 'react';

import { defineMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { css, cssMap, jsx } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import {
	Card,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { ACTION_SUBJECT_PREFIX, EVENT_SOURCE } from '../../constants/analytics';
import { all1pProductKeys, all3pProductKeys } from '../../constants/products';
import {
	LinkDocContentType,
	type ResultClickedCallback,
	type ResultClickedCallbackParams,
} from '../../constants/types';
import { isAuxClick } from '../../utils/clicks';

const fallbackComponentStyles = css({
	display: 'flex',
	gap: token('space.050'),
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
});

const styles = css({
	position: 'relative',
	paddingLeft: token('space.150'),
});

const cardList = cssMap({
	base: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.250'),
	},
	hidden: {
		opacity: 0,
		position: 'absolute',
		top: 0,
	},
	appended: {
		paddingTop: token('space.050'),
	},
	notAppended: {
		paddingTop: token('space.200'),
	},
});

/**
 * Smart cards lazy load when they are in the viewport. This is a behavior that is
 * not desired in this experience. To circumvent this, we set the position of the
 * card to absolute and hide it until the link is resolved. This allows the card to
 * be rendered in the DOM without lazy loading, while still being hidden from view
 * until the link is resolved.
 */
const card = cssMap({
	hidden: {
		opacity: 0,
		position: 'absolute',
		top: 0,
	},
});

const skeleton = cssMap({
	grid: {
		display: 'grid',
		gap: token('space.100'),
		gridTemplateAreas: `
			"icon title"
			"icon meta"
		`,
		gridTemplateColumns: 'min-content 1fr',
		gridTemplateRows: 'auto auto',
	},
	iconArea: {
		gridArea: 'icon',
	},
	titleArea: {
		gridArea: 'title',
	},
	metaArea: {
		gridArea: 'meta',
	},
});

type LinkedAttachmentsProps = {
	links: string[];
	isLinkedResults?: boolean;
	indexOffset?: number;
	parentPosition?: number;
	onResultClicked?: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
};

export const LinkAttachments: ({
	links,
	isLinkedResults,
	indexOffset,
	parentPosition,
	onResultClicked,
	onResultContextMenu,
}: LinkedAttachmentsProps) => JSX.Element = ({
	links = [],
	isLinkedResults,
	indexOffset,
	parentPosition,
	onResultClicked,
	onResultContextMenu,
}: LinkedAttachmentsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [linksLoading, setLinksLoading] = useState(links);
	const [linkProductKeys, setLinkProductKeys] = useState<Map<string, string>>(new Map());

	const cards = links.map((link, index) => {
		const resolveLink = (productKey?: string) => {
			if (productKey) {
				setLinkProductKeys((prev) => {
					// Only update if the value has changed to prevent infinite re-renders
					if (prev.get(link) !== productKey) {
						const newMap = new Map(prev);
						newMap.set(link, productKey);
						return newMap;
					}
					return prev;
				});
			}
			if (linksLoading.includes(link)) {
				setLinksLoading((prev) => prev.filter((l) => l !== link));
			}
		};

		return (
			<div
				css={linksLoading.length && card.hidden}
				key={link}
				role="button"
				tabIndex={0}
				onContextMenu={(e) => {
					e.preventDefault();
					if (isLinkedResults) {
						const productKey = linkProductKeys.get(link);
						const fanoutProduct = getProductKey(productKey);

						onResultContextMenu?.({
							sublistPosition: index + (indexOffset ?? 0),
							linkDocContentType: LinkDocContentType.extractedLink,
							fanoutProduct,
						} satisfies ResultClickedCallbackParams);
					}
				}}
				onAuxClick={(e: MouseEvent<HTMLDivElement>) => {
					if (isAuxClick(e) && isLinkedResults && fg('add-middle-click-event-fanout-results')) {
						const productKey = linkProductKeys.get(link);
						const fanoutProduct = getProductKey(productKey);

						onResultClicked?.({
							sublistPosition: index + (indexOffset ?? 0),
							linkDocContentType: LinkDocContentType.extractedLink,
							fanoutProduct,
							isAuxClick: true,
							tmpAuxDiagnostics: {
								button: e.button,
								nativeButton: e.nativeEvent?.button,
								eventType: e.type,
								isTrusted: e.nativeEvent?.isTrusted,
								metaKey: e.metaKey,
								ctrlKey: e.ctrlKey,
							},
						} satisfies ResultClickedCallbackParams);
					}
				}}
			>
				<Card
					url={link}
					appearance="inline"
					ui={{
						hideElevation: true,
						hideBackground: true,
						hidePadding: true,
						size: SmartLinkSize.Medium,
					}}
					onResolve={({ extensionKey }) => {
						resolveLink(extensionKey);
					}}
					onError={({ status }) => {
						resolveLink();
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'error',
								actionSubject: `${ACTION_SUBJECT_PREFIX}SearchResultSmartCard`,
								source: EVENT_SOURCE,
								attributes: {
									status,
								},
							},
						}).fire();
					}}
					onClick={() => {
						window.open(link, '_blank', 'noopener,noreferrer');
						const productKey = linkProductKeys.get(link);
						const fanoutProduct = getProductKey(productKey);

						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: `${ACTION_SUBJECT_PREFIX}SearchResultSmartCard`,
								source: EVENT_SOURCE,
								attributes: {
									position: parentPosition,
									sublistPosition: indexOffset !== undefined ? index + indexOffset : undefined,
								},
							},
						}).fire();

						if (isLinkedResults) {
							onResultClicked?.({
								sublistPosition: index + (indexOffset ?? 0),
								linkDocContentType: LinkDocContentType.extractedLink,
								fanoutProduct,
							} satisfies ResultClickedCallbackParams);
						}
					}}
					placeholder=" "
					fallbackComponent={() => (
						<div css={fallbackComponentStyles}>
							<LinkIcon label="" size="small" />
							<Anchor href={link} target="_blank" rel="noopener noreferrer">
								{link}
							</Anchor>
						</div>
					)}
				>
					<TitleBlock
						hideRetry
						hideIconLoadingSkeleton
						maxLines={1}
						metadata={[{ name: ElementName.State }]}
						subtitle={[{ name: ElementName.CreatedBy }, { name: ElementName.ModifiedOn }]}
						metadataPosition={SmartLinkPosition.Top}
						position={SmartLinkPosition.Top}
					/>
				</Card>
			</div>
		);
	});

	return (
		<div css={styles}>
			{linksLoading.length > 0 && (
				<div css={[cardList.base]}>
					{links.map((link) => (
						<div key={link} css={skeleton.grid}>
							<div css={skeleton.iconArea}>
								<Skeleton height="16px" width="16px" borderRadius={token('radius.small')} isShimmering />
							</div>
							<div css={skeleton.titleArea}>
								<Skeleton height="16px" width="50%" isShimmering />
							</div>
							<div css={skeleton.metaArea}>
								<Skeleton height="16px" width="85%" isShimmering />
							</div>
						</div>
					))}
				</div>
			)}
			<div
				css={[
					cardList.base,
					linksLoading.length && cardList.hidden,
					isLinkedResults ? cardList.appended : cardList.notAppended,
				]}
			>
				{cards}
			</div>
		</div>
	);
};

export const getLinksFromText: (text: string) => string[] = (text: string): string[] => {
	const urlPattern = /(https?:\/\/[^\s|>)\]]+)/g;
	const invalidEndingPattern = /\.{2,}|[.,;!?]+$/;

	const cleanedText = text.replace(/@@@hl@@@/g, '').replace(/@@@endhl@@@/g, '');

	let matches = cleanedText.match(urlPattern)?.map(String) || [];

	// Check if the text ends with a URL - if so, exclude it as it might be truncated
	if (matches.length > 0) {
		const lastMatch = matches[matches.length - 1];
		const lastMatchIndex = cleanedText.lastIndexOf(lastMatch);
		const textEndsWithLastMatch = lastMatchIndex + lastMatch.length === cleanedText.length;

		// If the text ends with a URL, exclude the last url match
		if (textEndsWithLastMatch) {
			matches = matches.slice(0, -1);
		}
	}

	// Filter out URLs that end with invalid patterns like "...", ".", ",", etc.
	const validMatches = matches.filter((url) => !invalidEndingPattern.test(url));

	// Deduplicate (helpful for markdown like [https://example.com](https://example.com) )
	return [...new Set(validMatches)];
};

const getProductKey = (key?: string): string => {
	if (!key) {
		return 'unknown';
	}

	const foundKey1P = all1pProductKeys.find((productKey) => key.includes(productKey));
	if (foundKey1P) {
		return foundKey1P;
	}

	const foundKey3P = all3pProductKeys.find((productKey) => key.includes(productKey));
	if (foundKey3P) {
		return foundKey3P;
	}

	return 'unknown';
};

export const buttonLabel: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'link-attachments.button.label',
	defaultMessage: '{count, plural, one {# link} other {# links}}',
	description: 'Use to describe the number of links shared in a conversation',
});
